<script setup lang="ts">
import { DisplayType } from '@/atoms/BaseTag.vue';
import LocalIcon from '@/atoms/LocalIcon.vue';
import BaseLabel from '@/atoms/BaseLabel.vue';
import BaseButton, { BaseButtonProps } from '@/atoms/BaseButton.vue';
import { cva } from 'class-variance-authority';
import { displayTypeCva } from '@/atoms/display-type-cva';
import { computed } from 'vue';
import { cn } from '@/helpers/tailwind-helpers';

export type BaseAlertProps = {
  type: DisplayType;
  title: string;
  description?: string;
  firstActionButtonProps?: BaseButtonProps;
  secondActionButtonProps?: BaseButtonProps;
};

const props = defineProps<BaseAlertProps>();

const baseAlert = cva('w-full flex outline -outline-offset-1 rounded-lg p-3', {
  variants: {
    type: displayTypeCva,
  },
  defaultVariants: {
    type: 'outline',
  },
});

const classes = computed(() => cn(baseAlert(props)));
</script>

<template>
  <div :class="classes">
    <div class="flex justify-center items-center h-6 w-6">
      <LocalIcon
        v-if="type === 'error'"
        name="circle-x-filled"
        size="small"
        class="max-w-4 max-h-4"
      />
      <LocalIcon v-else-if="type === 'success'" name="check-circle" size="small" />
      <LocalIcon v-else name="info-circle" size="small" />
    </div>
    <div class="flex flex-col w-full ml-1">
      <BaseLabel preset="medium" :text="title" />
      <BaseLabel v-if="description" preset="small" color="secondary" :text="description" />
    </div>
    <div class="flex space-x-2 ml-4 items-center">
      <BaseButton size="small" v-if="firstActionButtonProps" v-bind="firstActionButtonProps" />
      <BaseButton size="small" v-if="secondActionButtonProps" v-bind="secondActionButtonProps" />
    </div>
  </div>
</template>
